import { RouterState } from '@ngrx/router-store';
import { ActionReducer, MetaReducer } from '@ngrx/store';

// @priva packages
import { PrivaSegmentConfig } from '@priva/analytics/segment';
import { PrivaAuthHttpOptions } from '@priva/auth/http';
import { PrivaLocalizationConfig, PrivaLocalizationOptions } from '@priva/localization';
import { PrivaPreferencesOptions } from '@priva/preferences/core';
import { PrivaToggleRouter } from '@priva/utilities/feature-toggles';
import { AppConfiguration } from '@priva/utilities/layout';

import { AppConfigurationService } from 'app/shared/app-configuration';

// Environment
import { buildInfo, environment } from '../environments/environment';
import { SiteState } from './shared/site/site.reducer';
import { AppState } from './state/app.state';

export const localizationOptions: PrivaLocalizationConfig = {
    options: {
        provide: PrivaLocalizationOptions,
        deps: [AppConfigurationService],
        useFactory: (configService: AppConfigurationService) => {
            const uris = configService.configuration.uris;
            const options = new PrivaLocalizationOptions();
            options.path = 'assets/translations';
            options.version = buildInfo.version;
            options.availableLanguagesResource = uris.preferencesGetAvailableLanguages;
            options.defaultLanguage = 'en-US'; // 'en-US' is default when this setting is omitted
            options.translationFileSuffix = '.json';

            return options;
        },
    },
};

export function getToggleRouter(configService: AppConfigurationService): PrivaToggleRouter {
    return new PrivaToggleRouter(configService.configuration.features || []);
}

export function getPreferencesOptions(configService: AppConfigurationService): PrivaPreferencesOptions {
    return {
        preferenceApi: configService.configuration.uris.consolidatedPreferences,
        consolidatedPreferences: configService.configuration.uris.consolidatedPreferences,
    };
}

/**
 * Priva Auth options for PKCE code flow
 */
export function getCustomAuthOptions(configService: AppConfigurationService): PrivaAuthHttpOptions {
    const options = new PrivaAuthHttpOptions();
    options.bffConfig = configService.configuration.authentication?.bffConfig || { csrfValue: undefined };
    return options;
}

/**
 * Priva AppShell App base config
 */
export function getAppShellConfig(configService: AppConfigurationService): AppConfiguration {
    return AppConfiguration.from(configService.configuration);
}

/**
 * Priva Analytics - Segment config
 */
export function getAnalyticsSegmentConfig(configService: AppConfigurationService): PrivaSegmentConfig {
    const analytics = configService.configuration.analytics;
    const segment = analytics?.segment;
    const apiKey = segment?.apiKey ?? '';

    return new PrivaSegmentConfig(apiKey, {
        appName: 'Analytics',
        loadOnInitialization: apiKey !== undefined && apiKey !== '',
        trackInternalUsers: segment && !!segment.trackInternalUsers,
    });
}

export interface RootState {
    app: AppState;
    site: SiteState;
    router?: RouterState;
}

export function debug(r: ActionReducer<any>): ActionReducer<any> {
    return (state, action) => {
        return r(state, action);
    };
}

/**
 * Define which endpoints calls should contain the `Authorization Bearer` header
 */
export function customUrlValidationFunction(url: string) {
    // Send Bearer when in develop mode and running on mock server
    if (!environment.production && !!url.match(/\/api/g)) {
        return true;
    } else {
        // In production send Bearer with all requests going to *.priva.com/api or *.mypriva.com/api
        return !!url.match(/^(?:https:\/\/)(.*\.)?(priva|mypriva).com\/api/g);
    }
}

export const metaReducers: MetaReducer<any>[] = environment.production ? [] : [debug];
